import React, { useEffect, useRef, useState } from 'react';

/**
 * HeroOffice — the hero's backdrop: the office view's own 3D scene in its
 * generic backdrop mode (the same scene behind Zibby Studio's start-up screen),
 * vendored into public/office by scripts/vendor-office.js.
 *
 * It sits behind the whole hero, from the top of the page to the bottom of
 * `layoutRef` (the split layout), behind the install bar and the sign-in card.
 * The scene is only asked for once the page has painted, and never on narrow
 * screens (the hero there is just the card), so it costs the first paint
 * nothing. The office draws its own still while the scene loads, and stays on
 * that still where WebGL cannot run. Night always: the homepage is dark.
 *
 * The office sits just under the hero's call to action (the element marked
 * `data-office-anchor`, measured inside the layout), a little to its right, so
 * the building fills the floor below it and stays clear of the card. The anchor
 * moves with the viewport, so a fixed point would put the office behind it.
 * The backdrop takes a new focus through its hash (no reload: it eases there).
 *
 * The scene is drawn only in a FRAME around the building, not across the whole
 * hero: on a big retina screen a full-hero scene is so many pixels that the
 * office drops its own resolution to keep up and the picture goes soft. The
 * building's size follows the frame's HEIGHT (the office fits a fixed height of
 * the world to its view), so the frame keeps the hero's height up to FRAME_MAX_H
 * and only as much width as the building needs; its edges fade into the page,
 * and outside it the hero is just the dark ground.
 */
const BASE = '/office/office.html?mode=backdrop';
const WIDE = '(min-width: 961px)';
const DEFAULT_FOCUS = { x: 0.5, y: 0.56 };

// tilt: the page already fades the scene's bottom into itself, so the office's own blur bands stay light.
const sceneUrl = (focus) => `${BASE}#time=night&tilt=0.25&focus=${focus.x},${focus.y}`;

const FRAME_MAX_H = 1100;      // px: tall enough for a big building, small enough to stay sharp
const FRAME_ASPECT = 1.45;     // frame width per unit of height: the building plus a margin of grass

/** Where the building goes (just under the anchor, a little right of its middle), in hero px. */
function anchorPoint(layout, root) {
  const box = root.getBoundingClientRect();
  const anchor = layout.querySelector('[data-office-anchor]')?.getBoundingClientRect();
  if (!anchor) return null;
  return {
    x: anchor.left + anchor.width * 0.62 - box.left,
    // The hero is taller than a small screen: keep the building in what is
    // actually on screen, not low in the (partly off-screen) hero.
    y: Math.min(anchor.bottom + 230, window.innerHeight * 0.72) - box.top,
  };
}

/** The frame the scene is drawn in (hero px), and the office's focus inside it (fractions). */
function frameFor(point, heroW, heroH) {
  const h = Math.min(heroH, FRAME_MAX_H);
  const w = Math.min(heroW, Math.round(h * FRAME_ASPECT));
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  const p = point || { x: heroW * DEFAULT_FOCUS.x, y: heroH * DEFAULT_FOCUS.y };
  const left = Math.round(clamp(p.x - w / 2, 0, heroW - w));
  const top = Math.round(clamp(p.y - h / 2, 0, heroH - h));
  const round = (v) => Math.round(clamp(v, 0.1, 0.9) * 1000) / 1000;
  return { left, top, width: w, height: h, focus: { x: round((p.x - left) / w), y: round((p.y - top) / h) } };
}

export default function HeroOffice({ layoutRef }) {
  const rootRef = useRef(null);
  const [height, setHeight] = useState(0);
  const [frame, setFrame] = useState(null);
  const [started, setStarted] = useState(false);
  const [shown, setShown] = useState(false);

  // Track the hero's height (page top → bottom of the layout) and where the office goes.
  useEffect(() => {
    const layout = layoutRef.current;
    const root = rootRef.current;
    if (!layout || !root || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => {
      const top = root.parentElement.getBoundingClientRect().top;
      const h = Math.round(layout.getBoundingClientRect().bottom - top);
      setHeight(h);
      const next = frameFor(anchorPoint(layout, root), root.clientWidth, h);
      setFrame((cur) => (cur && JSON.stringify(cur) === JSON.stringify(next) ? cur : next));
    };
    const ro = new ResizeObserver(measure);
    ro.observe(layout);
    window.addEventListener('resize', measure);
    measure();
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [layoutRef]);

  // Ask for the scene after the page has painted, and only on wide screens.
  useEffect(() => {
    if (!window.matchMedia?.(WIDE).matches) return undefined;
    let idle = 0;
    const start = () => {
      idle = (window.requestIdleCallback || ((fn) => setTimeout(fn, 200)))(() => setStarted(true), { timeout: 1500 });
    };
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
    return () => {
      window.removeEventListener('load', start);
      (window.cancelIdleCallback || clearTimeout)(idle);
    };
  }, []);

  return (
    <div
      ref={rootRef}
      className={`hero-office${shown ? ' is-shown' : ''}`}
      style={height ? { height } : undefined}
      aria-hidden="true"
    >
      {started && frame && (
        <iframe
          className="hero-office__scene"
          style={{ left: frame.left, top: frame.top, width: frame.width, height: frame.height }}
          src={sceneUrl(frame.focus)}
          title="Animated office backdrop"
          tabIndex={-1}
          loading="eager"
          onLoad={() => setShown(true)}
        />
      )}
      <div className="hero-office__shade" />
    </div>
  );
}
